import type * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface DetailSectionProps {
  /** Section heading rendered inside CardHeader. */
  title: string;
  /** Optional right-aligned controls in the header (e.g. an inline edit button). */
  headerActions?: React.ReactNode;
  /**
   * When true, render the title bolder + slightly larger so the section header
   * stands out. Opt-in so existing detail pages are unchanged.
   */
  boldTitle?: boolean;
  /** Section body. */
  children: React.ReactNode;
  className?: string;
}

/**
 * Standard card-shaped section used on every detail page. Wraps `<Card>` with
 * a header + content split and a slot for header-level actions (so consumers
 * don't have to remember the right `Card`/`CardHeader`/`CardTitle` ladder).
 */
export function DetailSection({
  title,
  headerActions,
  boldTitle,
  children,
  className,
}: DetailSectionProps) {
  return (
    <Card className={className}>
      <CardHeader className={cn("flex flex-row items-center justify-between gap-2", "[&>*]:m-0")}>
        <CardTitle className={cn("text-base", boldTitle && "font-bold text-lg")}>{title}</CardTitle>
        {headerActions}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

interface MetadataRowProps {
  label: string;
  /** Plain string value; pass `children` instead for non-string content. */
  value?: string | number | null;
  /** Non-string content (e.g. a badge or link). Takes precedence over `value`. */
  children?: React.ReactNode;
  /** Inline node rendered right after the label (e.g. a pending-change icon). */
  adornment?: React.ReactNode;
  className?: string;
}

/**
 * Single label-over-value pair, used inside `DetailSection` to render record
 * metadata. Renders an em-dash for nullish/empty string values so empty
 * fields read as deliberate rather than missing data.
 */
export function MetadataRow({ label, value, children, adornment, className }: MetadataRowProps) {
  const display = children ?? (value === null || value === undefined || value === "" ? "—" : value);
  return (
    <div className={cn("text-sm", className)}>
      <span className="inline-flex items-center gap-1 font-bold text-muted-foreground">
        {label}
        {adornment}
      </span>
      <div className="mt-1">{display}</div>
    </div>
  );
}
